import React, { useState, useEffect, useMemo } from 'react';
import { Home, Search, Plus, Bookmark, User, MessageCircle, Briefcase, Calendar, BarChart3, Package, HelpCircle, Bell, Users, Shield, Menu, X, Settings, LogOut, type LucideIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import SettingsModal from './SettingsModal';
import CreatePost from './CreatePost';
import { notificationsService } from '../services/notificationsService';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { FALLBACK_LANGUAGE } from '../i18n/translations';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationProps {
  onCreateClick?: () => void;
}

// Helper component extracted to prevent re-renders
const NavigationItem: React.FC<{
  item: { id: string; label: string; icon: LucideIcon | (() => React.ReactNode); showForAll: boolean; path: string; badge?: number; };
  onClick: (item: { id: string; path: string; }) => void;
  isActive: boolean;
  className?: string;
}> = ({ item, onClick, isActive, className = "" }) => {
  const Icon = item.icon;

  // Special styling for Create button
  if (item.id === 'create') {
    return (
      <button
        type="button"
        style={{ zIndex: 50, position: 'relative' }}
        onClick={() => {
          console.log('Create button clicked in NavigationItem');
          onClick(item);
        }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 ${isActive
          ? 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white shadow-xl scale-105 animate-pulse'
          : 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white shadow-lg hover:shadow-xl hover:from-purple-600 hover:via-pink-600 hover:to-orange-500'
          }`}
      >
        <div className="relative">
          <Icon size={22} className="drop-shadow-sm" />
          {item.badge && item.badge > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
          {/* Sparkle effect */}
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-white/30 rounded-full animate-ping"></div>
        </div>
        <span className="font-bold text-lg drop-shadow-sm">{item.label}</span>
        <div className="ml-auto">
          <span className="text-lg">✨</span>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${className} ${isActive
        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105'
        : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50 hover:scale-105'
        }`}
    >
      <div className="relative">
        <Icon size={20} className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110 group-hover:rotate-3'}`} />
        {item.badge && item.badge > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </div>
      <span className="font-medium">{item.label}</span>
    </button>
  );
};

const Navigation: React.FC<NavigationProps> = ({ onCreateClick }) => {
  const { user, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settingsTab, setSettingsTab] = useState<string>('account');
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const logoSrc = isDark
    ? 'https://soartistic.com/wp-content/uploads/2025/05/white_logo_transparent_background-364x300.png'
    : 'https://soartistic.com/wp-content/uploads/2025/06/logo-negro.svg';

  const isArtist = user?.user_type === 'artist' || user?.user_type === 'admin';
  const isAdmin = user?.user_type === 'admin';
  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';

  const nextLanguage = useMemo(() => {
    const currentIndex = availableLanguages.indexOf(language);
    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % availableLanguages.length
      : 0;
    return availableLanguages[nextIndex] ?? FALLBACK_LANGUAGE;
  }, [availableLanguages, language]);

  const nextLanguageLabel = t(`language.${nextLanguage}`, nextLanguage.toUpperCase());
  const switchLabel = t('language.switchToNext', `Switch to ${nextLanguageLabel}`)
    .replace('{language}', nextLanguageLabel);

  const toggleLanguage = () => setLanguage(nextLanguage);

  useEffect(() => {
    if (user) {
      loadUnreadNotifications();

      // Subscribe to notification changes
      const subscription = notificationsService.subscribeToNotifications(
        user.id,
        () => {
          loadUnreadNotifications();
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  useEffect(() => {
    // Listen for settings modal open events
    const handleOpenSettings = (event: CustomEvent) => {
      const { tab } = event.detail;
      setSettingsTab(tab || 'account');
      setShowSettingsModal(true);
    };

    window.addEventListener('openSettings', handleOpenSettings as EventListener);

    return () => {
      window.removeEventListener('openSettings', handleOpenSettings as EventListener);
    };
  }, []);

  const loadUnreadNotifications = async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Error loading unread notifications:', err);
    }
  };

  interface NavItem {
    id: string;
    label: string;
    icon: LucideIcon | (() => React.ReactNode);
    showForAll: boolean;
    path: string;
    badge?: number;
  }

  const menuItems: NavItem[] = useMemo(() => ([
    { id: 'home', label: t('navigation.home', 'Inicio'), icon: Home, showForAll: true, path: '/home' },
    { id: 'explore', label: t('navigation.explore', 'Explorar'), icon: Search, showForAll: true, path: '/explore' },
    { id: 'create', label: t('navigation.create', 'Crear'), icon: Plus, showForAll: isArtist, path: '/create' }, // Path for create is special
    { id: 'saved', label: t('navigation.saved', 'Guardado'), icon: Bookmark, showForAll: true, path: '/saved' },
    { id: 'community', label: t('navigation.community', 'Comunidad'), icon: Users, showForAll: true, path: '/community' },
    { id: 'profile', label: t('navigation.profile', 'Perfil'), icon: User, showForAll: true, path: '/profile' },
  ]), [isArtist, t]);

  const secondaryItems: NavItem[] = useMemo(() => ([
    { id: 'collaborations', label: t('navigation.collaborations', 'Colaboraciones'), icon: Briefcase, showForAll: isArtist, path: '/collaborations' },
    { id: 'messages', label: t('navigation.messages', 'Mensajes'), icon: MessageCircle, showForAll: true, path: '/messages' },
    { id: 'events', label: t('navigation.events', 'Eventos'), icon: Calendar, showForAll: true, path: '/events' },
    { id: 'notifications', label: t('navigation.notifications', 'Notificaciones'), icon: Bell, showForAll: true, badge: unreadCount, path: '/notifications' },
  ]), [isArtist, t, unreadCount]);

  const tertiaryItems: NavItem[] = useMemo(() => ([
    { id: 'analytics', label: t('navigation.analytics', 'Analíticas'), icon: BarChart3, showForAll: isArtist, path: '/analytics' },
    { id: 'sales', label: t('navigation.sales', 'Ventas'), icon: Package, showForAll: isArtist, path: '/sales' },
    { id: 'help', label: t('navigation.help', 'Ayuda'), icon: HelpCircle, showForAll: true, path: '/help' },
  ]), [isArtist, t]);

  const adminItems: NavItem[] = useMemo(() => ([
    { id: 'admin', label: t('navigation.admin', 'Administración'), icon: Shield, showForAll: isModerator, path: '/admin' },
    { id: 'ads', label: t('navigation.ads', 'SoArtistic Ads'), icon: BarChart3, showForAll: isAdmin || isArtist, path: '/ads' },
  ]), [isAdmin, isArtist, isModerator, t]);

  const handleTabClick = async (item: { id: string; path: string; }) => {
    console.log('handleTabClick called for:', item.id);
    
    if (item.id === 'ads') {
      // Open window immediately to prevent popup blocker
      const adsWindow = window.open('about:blank', '_blank');
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const adsUrl = new URL('https://ads.soartistic.com');
        
        if (session) {
          // Pass session tokens in URL hash for auto-authentication
          adsUrl.hash = `access_token=${session.access_token}&refresh_token=${session.refresh_token}&type=recovery`;
        }
        
        if (adsWindow) {
          adsWindow.location.href = adsUrl.toString();
        } else {
          // Fallback if window.open failed (rare if triggered by click)
          window.location.href = adsUrl.toString();
        }
      } catch (err) {
        console.error('Error handling ads navigation:', err);
        if (adsWindow) {
          adsWindow.location.href = 'https://ads.soartistic.com';
        }
      }
      
      setShowMobileMenu(false);
      return;
    }

    if (item.id === 'create') {
      if (onCreateClick) {
        console.log('Calling onCreateClick prop');
        onCreateClick();
      } else {
        console.warn('onCreateClick prop missing');
      }
    } else {
      navigate(item.path);
    }
    setShowMobileMenu(false);

    // Mark notifications as read when visiting notifications tab
    if (item.id === 'notifications' && unreadCount > 0) {
      setTimeout(() => {
        loadUnreadNotifications();
      }, 1000);
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 w-64 h-full bg-white border-r border-gray-200 z-40">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img
                src={logoSrc}
                alt="SoArtistic"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">SoArtistic</h1>
                <p className="text-xs text-gray-500">{t('navigation.tagline', 'Plataforma de Arte')}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Main Navigation */}
            <div className="space-y-2">
              <div className="space-y-2">
                {menuItems.filter(item => item.showForAll).map((item) => {
                  if (item.id === 'create') {
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          console.log('Create button clicked - opening modal');
                          setShowCreatePost(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white shadow-xl scale-105 animate-pulse"
                      >
                        <div className="relative">
                          <Plus size={22} className="drop-shadow-sm" />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-white/30 rounded-full animate-ping"></div>
                        </div>
                        <span className="font-bold text-lg drop-shadow-sm">{item.label}</span>
                        <div className="ml-auto">
                          <span className="text-lg">✨</span>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <NavigationItem
                      key={item.id}
                      item={item}
                      onClick={handleTabClick}
                      isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                    />
                  );
                })}
              </div>
            </div>

            {/* Secondary Navigation */}
            {secondaryItems.some(item => item.showForAll) && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-4">
                  {t('navigation.section.activity', 'Actividad')}
                </h3>
                {secondaryItems.filter(item => item.showForAll).map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    onClick={handleTabClick}
                    isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                  />
                ))}
              </div>
            )}

            {/* Tertiary Navigation */}
            {tertiaryItems.some(item => item.showForAll) && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-4">
                  {t('navigation.section.tools', 'Herramientas')}
                </h3>
                {tertiaryItems.filter(item => item.showForAll).map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    onClick={handleTabClick}
                    isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                  />
                ))}
              </div>
            )}

            {/* Admin Navigation */}
            {adminItems.some(item => item.showForAll) && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-4">
                  {t('navigation.section.admin', 'Administración')}
                </h3>
                {adminItems.filter(item => item.showForAll).map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    onClick={handleTabClick}
                    className="border-2 border-red-200 hover:border-red-300"
                    isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                  />
                ))}
              </div>
            )}
          </div>

          {/* User Profile & Settings */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                alt={user?.full_name}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{user?.full_name}</p>
                <p className="text-sm text-gray-500 truncate">@{user?.username}</p>
                {user?.user_type === 'admin' && (
                  <span className="text-xs text-red-600 font-medium">👑 {t('navigation.adminRole', 'Administrador')}</span>
                )}
                {user?.user_type === 'moderator' && (
                  <span className="text-xs text-blue-600 font-medium">🛡️ {t('navigation.moderatorRole', 'Moderador')}</span>
                )}
                {user?.user_type === 'artist' && (
                  <span className="text-xs text-purple-600 font-medium">
                    🎨 {t('navigation.artistRole', 'Modo artista')}
                  </span>
                )}
                {user?.user_type === 'visitor' && (
                  <span className="text-xs text-gray-600 font-medium">
                    👀 {t('navigation.visitorRole', 'Modo visitante')}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={toggleLanguage}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                aria-label={switchLabel}
                title={switchLabel}
              >
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{t('language.switchLabel', 'Idioma')}</span>
                  <span className="text-xs text-gray-500">{switchLabel}</span>
                </div>
                <span className="ml-auto text-xs font-semibold uppercase bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {language.toUpperCase()}
                </span>
              </button>

              <button
                onClick={() => {
                  setSettingsTab('account');
                  setShowSettingsModal(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all group"
              >
                <Settings size={18} className="transition-transform duration-500 group-hover:rotate-90" />
                <span className="text-sm font-medium">{t('navigation.settings', 'Configuración')}</span>
              </button>

              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all group"
              >
                <LogOut size={18} className="transition-transform group-hover:translate-x-1" />
                <span className="text-sm font-medium">{t('navigation.logout', 'Cerrar sesión')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe">
        <div className="flex items-center justify-around py-2">
          {menuItems.slice(0, 4).filter(item => item.showForAll).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            // Special styling for Create button on mobile
            if (item.id === 'create') {
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item)}
                  className={`flex flex-col items-center gap-1 py-2 px-3 rounded-xl transition-all touch-target transform ${isActive
                    ? 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white shadow-xl scale-110 animate-pulse'
                    : 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white shadow-lg hover:scale-105 hover:shadow-xl'
                    }`}
                >
                  <div className="relative">
                    <Icon size={24} className="drop-shadow-sm" />
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {/* Sparkle effect for mobile */}
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-white/30 rounded-full animate-ping"></div>
                  </div>
                  <span className="text-xs font-bold drop-shadow-sm">{item.label}</span>
                  {/* Sparkle indicator */}
                  <div className="absolute -top-1 -right-1 text-xs">✨</div>
                </button>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item)}
                className={`flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-all touch-target group ${isActive
                  ? 'text-purple-600 bg-purple-50'
                  : 'text-gray-600'
                  }`}
              >
                <div className="relative">
                  <Icon size={20} className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110 group-active:scale-95'}`} />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center animate-bounce">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(true)}
            className="flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-all touch-target text-gray-600 relative"
          >
            <div className="relative">
              <Menu size={20} />
              {(unreadCount > 0 || isModerator) && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </div>
            <span className="text-xs font-medium">{t('navigation.more', 'Más')}</span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Modal */}
      {showMobileMenu && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-3xl modal-content">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{t('navigation.menuTitle', 'Menú')}</h2>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                <img
                  src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                  alt={user?.full_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              <div>
                <p className="font-semibold text-gray-900">{user?.full_name}</p>
                <p className="text-sm text-gray-500">@{user?.username}</p>
                {user?.user_type === 'admin' && (
                  <span className="text-xs text-red-600 font-medium">👑 {t('navigation.adminRole', 'Administrador')}</span>
                )}
                {user?.user_type === 'moderator' && (
                  <span className="text-xs text-blue-600 font-medium">🛡️ {t('navigation.moderatorRole', 'Moderador')}</span>
                )}
                {user?.user_type === 'artist' && (
                  <span className="text-xs text-purple-600 font-medium">
                    🎨 {t('navigation.artistRole', 'Modo artista')}
                  </span>
                )}
                {user?.user_type === 'visitor' && (
                  <span className="text-xs text-gray-600 font-medium">
                    👀 {t('navigation.visitorRole', 'Modo visitante')}
                  </span>
                )}
              </div>
            </div>

              {/* All Menu Items */}
              <div className="space-y-4">
                {/* Profile Button - First */}
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      handleTabClick(menuItems.find(item => item.id === 'profile')!);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${location.pathname === '/profile'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50 hover:scale-105'
                      }`}
                  >
                    <div className="relative">
                      <User size={20} />
                    </div>
                    <span className="font-medium">{t('navigation.myProfile', 'Mi Perfil')}</span>
                  </button>
                </div>

                {/* Secondary Items */}
                <div className="space-y-2">
                  {secondaryItems.filter(item => item.showForAll).map((item) => (
                    <NavigationItem
                      key={item.id}
                      item={item}
                      onClick={handleTabClick}
                      isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                    />
                  ))}
                </div>

                {/* Tertiary Items */}
                {tertiaryItems.some(item => item.showForAll) && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700 px-4">{t('navigation.section.tools', 'Herramientas')}</h3>
                    {tertiaryItems.filter(item => item.showForAll).map((item) => (
                      <NavigationItem
                        key={item.id}
                        item={item}
                        onClick={handleTabClick}
                        isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                      />
                    ))}
                  </div>
                )}

                {/* Admin Items */}
                {adminItems.some(item => item.showForAll) && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700 px-4">{t('navigation.section.admin', 'Administración')}</h3>
                    {adminItems.filter(item => item.showForAll).map((item) => (
                      <NavigationItem
                        key={item.id}
                        item={item}
                        onClick={handleTabClick}
                        className="border-2 border-red-200 hover:border-red-300"
                        isActive={location.pathname === item.path || (item.id === 'messages' && location.pathname.startsWith('/messages/'))}
                      />
                    ))}
                  </div>
                )}

                {/* Settings and Logout */}
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => {
                      setShowMobileMenu(false);
                      setSettingsTab('account');
                      setShowSettingsModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all group"
                  >
                    <Settings size={20} className="transition-transform duration-500 group-hover:rotate-90" />
                    <span className="font-medium">{t('navigation.settings', 'Configuración')}</span>
                  </button>

                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all group"
                  >
                    <LogOut size={20} className="transition-transform group-hover:translate-x-1" />
                    <span className="font-medium">{t('navigation.logout', 'Cerrar sesión')}</span>
                  </button>
                  <button
                    onClick={() => {
                      toggleLanguage();
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all"
                    aria-label={switchLabel}
                    title={switchLabel}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('language.switchLabel', 'Idioma')}</span>
                      <span className="text-xs text-gray-500">{switchLabel}</span>
                    </div>
                    <span className="ml-auto text-xs font-semibold uppercase bg-purple-100 text-purple-700 px-2 py-1 rounded">
                      {language.toUpperCase()}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          initialTab={settingsTab}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {/* Create Post Modal */}
      {showCreatePost && (
        <CreatePost
          onClose={() => setShowCreatePost(false)}
          onSubmit={() => {
            setShowCreatePost(false);
            navigate('/home');
          }}
        />
      )}
    </>
  );
};

export default Navigation;
